/**
 * Release 1 F5 — "Already in your library" upload prompt. After a file is hashed the gate asks the API for an exact
 * match; when one exists the upload waits for the user's choice (Skip by default / Upload anyway, optionally for
 * every remaining file of the batch). The store is tiny and framework-free so the decision logic is unit-testable.
 */
import { create } from 'zustand';
import type { AssetCard } from '@/lib/api/types';
import { hashPool, type HashGate } from '@/features/upload/hashPool';
import { uploadManager } from '@/features/upload/UploadManager';
import { useSessionStore } from '@/stores/session';
import { lookupAssetsBySha256 } from '@/lib/api/queries';

export type PromptChoice = 'skip' | 'upload';
export type PendingPrompt = { id: string; name: string; matches: AssetCard[]; resolve: (c: PromptChoice) => void };

export type UploadPromptState = {
  pending: Record<string, PendingPrompt>;
  /** remembered "apply to all" choice for the rest of the batch; cleared when the queue drains */
  applyAll: PromptChoice | null;
};

/** Pure decision step: resolve one prompt, optionally remember the choice and resolve everything else the same way. */
export function decidePrompt(state: UploadPromptState, id: string, choice: PromptChoice, applyToAll: boolean): { next: UploadPromptState; resolved: Array<{ id: string; choice: PromptChoice }> } {
  const resolved: Array<{ id: string; choice: PromptChoice }> = [];
  const pending = { ...state.pending };
  const target = pending[id];
  if (target) {
    delete pending[id];
    resolved.push({ id, choice });
  }
  if (applyToAll) {
    for (const p of Object.values(pending)) {
      delete pending[p.id];
      resolved.push({ id: p.id, choice });
    }
  }
  return { next: { pending, applyAll: applyToAll ? choice : state.applyAll }, resolved };
}

export const useUploadPromptStore = create<UploadPromptState>()(() => ({ pending: {}, applyAll: null }));

/** Ask the user (or answer immediately from a remembered "apply to all"). */
export function askDuplicatePrompt(p: Omit<PendingPrompt, 'resolve'>): Promise<PromptChoice> {
  const { applyAll } = useUploadPromptStore.getState();
  if (applyAll) return Promise.resolve(applyAll);
  return new Promise<PromptChoice>((resolve) => {
    useUploadPromptStore.setState((s) => ({ pending: { ...s.pending, [p.id]: { ...p, resolve } } }));
  });
}

export function decideDuplicatePrompt(id: string, choice: PromptChoice, applyToAll = false): void {
  const state = useUploadPromptStore.getState();
  const { next, resolved } = decidePrompt(state, id, choice, applyToAll);
  useUploadPromptStore.setState(next);
  for (const r of resolved) state.pending[r.id]?.resolve(r.choice);
}

/** Forget the remembered batch choice once nothing is waiting (called when the tray empties). */
export function resetDuplicatePrompts(): void {
  const s = useUploadPromptStore.getState();
  for (const p of Object.values(s.pending)) p.resolve('upload');
  useUploadPromptStore.setState({ pending: {}, applyAll: null });
}

type Lookup = (wsId: string, sha256: string) => Promise<{ items: Array<{ asset: AssetCard; match: 'exact' | 'near' }> }>;

/** The gate itself: exact matches only, member sessions only, never blocks on API trouble. */
export function makeDuplicateGate(lookup: Lookup = lookupAssetsBySha256, ask = askDuplicatePrompt, onSkip: (id: string) => void = (id) => uploadManager.cancel(id)): HashGate {
  return async (id, file, result) => {
    if (result.hash_kind !== 'full') return 'upload';
    const ws = useSessionStore.getState().workspace;
    if (!ws) return 'upload';
    let matches: AssetCard[];
    try {
      matches = (await lookup(ws.id, result.sha256)).items.filter((m) => m.match === 'exact').map((m) => m.asset);
    } catch {
      return 'upload';
    }
    if (matches.length === 0) return 'upload';
    const choice = await ask({ id, name: (file as File).name ?? 'file', matches });
    if (choice === 'skip') onSkip(id);
    return choice;
  };
}

let installed = false;
export function installDuplicateGate(): void {
  if (installed) return;
  installed = true;
  hashPool().setGate(makeDuplicateGate());
}
